/**
 * pcmTranscriptCapture — operator LOCAL-microphone → LINEAR16/16k segments for the human-call
 * transcript (Phase C). Taps the SAME mic MediaStreamTrack that is already being sent to the
 * RTCPeerConnection (WebAudio reads the track without taking ownership, so the peer still gets audio).
 *
 * Hard rules (match the backend Phase B contract):
 *   - captures ONLY the local microphone — never the remote/customer track, never mixed output.
 *   - emits mono LINEAR16 (audio/l16) @16kHz, bounded segments (<=30s, well under the 3MB cap).
 *   - never persists audio, never logs audio/transcript bytes.
 *   - upload is injected + async; capture never blocks or degrades media.
 *
 * Usage:
 *   const cap = createTranscriptCapture({ micTrack, upload, segmentPrefix, segmentSeconds });
 *   cap.start();            // after HUMAN_CALL_ACTIVE
 *   await cap.stop();       // on hang-up — flushes a final is_final segment
 */

const TARGET_RATE = 16000;
const DEFAULT_SEGMENT_SECONDS = 10;   // safely below the 30s / 3MB server limits
const MAX_QUEUE = 8;                  // bounded in-memory upload queue; excess => transcript partial

// FNV-1a 32-bit hex over the PCM bytes — a cheap stable idempotency checksum (no crypto/async).
function fnv1aHex(bytes) {
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i];
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return ('0000000' + h.toString(16)).slice(-8);
}

// Float32 [-1,1] mono @ inRate  ->  Int16 LE @ 16k (linear-interpolation resample).
function floatToInt16Downsampled(float32, inRate) {
  if (!float32 || float32.length === 0) return new Int16Array(0);
  if (inRate === TARGET_RATE) {
    const out = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      const s = Math.max(-1, Math.min(1, float32[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }
  const ratio = inRate / TARGET_RATE;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const idx = i * ratio;
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, float32.length - 1);
    const frac = idx - lo;
    let s = float32[lo] * (1 - frac) + float32[hi] * frac;
    s = Math.max(-1, Math.min(1, s));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function int16ToBase64(int16) {
  const bytes = new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  // btoa in browsers; Buffer in jsdom/node test env.
  if (typeof btoa === 'function') return btoa(binary);
  return Buffer.from(binary, 'binary').toString('base64');
}

/**
 * @param {object} o
 * @param {MediaStreamTrack} o.micTrack        the local mic track already added to the pc
 * @param {(segment:object)=>Promise} o.upload  authenticated POST of one segment; may reject
 * @param {string} o.segmentPrefix              stable id prefix, e.g. `${mediaSessionId}-op`
 * @param {number} [o.segmentSeconds]
 * @param {(reason:string)=>void} [o.onPartial] called when the bounded queue overflows / errors
 * @param {AudioContextClass} [o.AudioContextCtor] injectable for tests
 */
export function createTranscriptCapture(o) {
  const { micTrack, upload, segmentPrefix } = o;
  const segmentSeconds = o.segmentSeconds || DEFAULT_SEGMENT_SECONDS;
  const AC = o.AudioContextCtor || (typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext));
  let ctx = null, source = null, node = null;
  let acc = [];               // array of Int16Array chunks for the current segment
  let accSamples = 0;         // 16k samples accumulated
  let seq = 0;
  let segStartedAt = null;
  let inflight = 0;
  let running = false;
  let partialFlagged = false;

  function flagPartial(reason) {
    if (!partialFlagged && o.onPartial) { partialFlagged = true; try { o.onPartial(reason); } catch (_) {} }
  }

  async function emitSegment(isFinal) {
    if (accSamples <= 0) return;
    // concat chunks
    const merged = new Int16Array(accSamples);
    let off = 0;
    for (const c of acc) { merged.set(c, off); off += c.length; }
    acc = []; accSamples = 0;
    const startedAt = segStartedAt || new Date().toISOString();
    segStartedAt = null;
    const endedAt = new Date().toISOString();
    const bytes = new Uint8Array(merged.buffer, merged.byteOffset, merged.byteLength);
    const seg = {
      segment_id: `${segmentPrefix}-${seq}`,
      sequence_number: seq,
      mime: 'audio/l16',
      audio_base64: int16ToBase64(merged),
      duration_s: merged.length / TARGET_RATE,
      segment_started_at: startedAt,
      segment_ended_at: endedAt,
      is_final: !!isFinal,
      checksum: fnv1aHex(bytes),
    };
    seq += 1;
    if (inflight >= MAX_QUEUE) { flagPartial('queue_full'); return; }   // bounded — never unbounded storage
    inflight += 1;
    try {
      await upload(seg);
    } catch (_) {
      flagPartial('upload_failed');   // bounded retry is the caller's concern; we never buffer audio to disk
    } finally {
      inflight -= 1;
    }
  }

  function onAudio(ev) {
    if (!running) return;
    const inRate = (ctx && ctx.sampleRate) || 48000;
    const input = ev.inputBuffer.getChannelData(0);
    if (!segStartedAt) segStartedAt = new Date().toISOString();
    acc.push(floatToInt16Downsampled(input, inRate));
    accSamples += acc[acc.length - 1].length;
    if (accSamples >= segmentSeconds * TARGET_RATE) {
      // fire-and-forget; capture keeps running
      emitSegment(false);
    }
  }

  function start() {
    if (running || !AC || !micTrack) return;
    running = true;
    ctx = new AC();
    source = ctx.createMediaStreamSource(new MediaStream([micTrack]));
    // ScriptProcessor: deprecated but universally available and dependency-free (matches codebase).
    node = ctx.createScriptProcessor ? ctx.createScriptProcessor(4096, 1, 1)
                                     : ctx.createJavaScriptNode(4096, 1, 1);
    node.onaudioprocess = onAudio;
    source.connect(node);
    // Route through a muted gain to keep the graph pulling without local echo.
    const sink = ctx.createGain ? ctx.createGain() : null;
    if (sink) { sink.gain.value = 0; node.connect(sink); sink.connect(ctx.destination); }
    else { node.connect(ctx.destination); }
  }

  async function stop() {
    if (!running) return;
    running = false;
    try { if (node) node.onaudioprocess = null; } catch (_) {}
    try { if (source) source.disconnect(); } catch (_) {}
    try { if (node) node.disconnect(); } catch (_) {}
    await emitSegment(true);   // flush final segment
    try { if (ctx && ctx.state !== 'closed') await ctx.close(); } catch (_) {}
    ctx = source = node = null;
  }

  return { start, stop, _emitSegment: emitSegment, _floatToInt16Downsampled: floatToInt16Downsampled };
}

export const __test = { fnv1aHex, floatToInt16Downsampled, int16ToBase64 };
